// Callstack plugin.
import { was3xHold, was3xUnhold } from './operations'

// Call plugin.
import { OPERATIONS } from '../../call/interfaceNew/constants'

// Libraries.
import { call } from 'redux-saga/effects'

/**
 * Get the remote tracks of a Session that are affected by a remote operation.
 * @param {string} operation The remote operation type.
 * @param {MediaDiff} mediaDiff The difference between the SDP's media sections before/after the operation.
 * @param {Sesison} session A WebRTC-stack Session object.
 * @return {Array<string>} List of track IDs.
 */
export default function * getAffectedRemoteTracks (operation, mediaDiff, session) {
  /*
   * If a media section was removed, then this is not a normal WebRTC negotiation. This
   *    is likely a complex operation where the PeerConnection was recreated. Calls are
   *    always on hold when a PeerConnection is recreated, so all remote tracks should
   *    be considered as "added".
   * The media sections of the SDP may not have IDs, which is another reason to simply
   *    "get all".
   */
  if (mediaDiff.removed.length > 0) {
    const affectedTracks = yield call(getAllRemoteTracks, session)
    return affectedTracks
  }

  let sectionIds
  switch (operation) {
    case OPERATIONS.ADD_MEDIA:
    case OPERATIONS.START_MOH:
      sectionIds = getAddedSectionIds(mediaDiff)
      break
    case OPERATIONS.REMOVE_MEDIA:
    case OPERATIONS.STOP_MOH:
      sectionIds = getRemovedSectionIds(mediaDiff)
      break
    case OPERATIONS.HOLD:
      sectionIds = was3xHold(mediaDiff) ? get3xHeldSectionIds(mediaDiff) : getHeldSectionIds(mediaDiff)
      break
    case OPERATIONS.UNHOLD:
      sectionIds = was3xUnhold(mediaDiff) ? get3xUnheldSectionIds(mediaDiff) : getUnheldSectionIds(mediaDiff)
      break
    default:
      // What do?
      sectionIds = []
  }

  const affectedTracks = yield call([session, 'getRemoteTrackIdsFromTransceivers'], sectionIds)

  return affectedTracks
}

/**
 * Get the media sections of an SDP where media was remobed by a remote "hold"
 *    SDK operation.
 * @param {MediaDiff} mediaDiff
 * @return {Array<string>} List of media section IDs.
 */
function getHeldSectionIds (mediaDiff) {
  const heldSections = []
  mediaDiff.changed.forEach(changedMedia => {
    const { media, changes } = changedMedia
    // We know this was a hold operation, so we only need to check that the
    //    media section stopped sending media to know that a track was removed here.
    if (changes.sending === 'STOP') {
      const id = typeof media.sectionId === 'undefined' ? media.sectionIndex : media.sectionId
      heldSections.push(id)
    }
  })
  return heldSections
}

/**
 * Get the media sections of an SDP where media was remobed by a remote "hold"
 *    SDK operation. This function covers the special cases for interop with 3.x/Mobile.
 *    Refer to `was3xHold` function for more details.
 * @param {MediaDiff} mediaDiff
 * @return {Array<string>} List of media section IDs.
 */
function get3xHeldSectionIds (mediaDiff) {
  const heldSections = []
  mediaDiff.changed.forEach(changedMedia => {
    const { media, changes } = changedMedia
    if (media.type === 'audio') {
      // Changed audio always stops being received by the remote endpoint.
      // Changed audio always continues being sent by the remote endpoint.
      if (changes.receiving === 'STOP' && changes.sending === 'SAME') {
        const id = typeof media.sectionId === 'undefined' ? media.sectionIndex : media.sectionId
        heldSections.push(id)
      }
    } else {
      // Changed video is never received afterwards.
      //     Either stopped or stayed "not receiving".
      if (changes.receiving === 'STOP' || (changes.receiving === 'SAME' && media.willReceive === false)) {
        const id = typeof media.sectionId === 'undefined' ? media.sectionIndex : media.sectionId
        heldSections.push(id)
      }
    }
  })

  // Edge-case: One-way video was being received, and the hold did not
  //    change the direction but just stopped the video being sent.
  mediaDiff.unchanged.forEach(media => {
    if (media.type === 'video' && media.willSend && !media.willReceive) {
      const id = typeof media.sectionId === 'undefined' ? media.sectionIndex : media.sectionId
      heldSections.push(id)
    }
  })

  return heldSections
}

/**
 * Get the media sections of an SDP where media was added by a remote "unhold"
 *    SDK operation.
 * @param {MediaDiff} mediaDiff
 * @return {Array<string>} List of media section IDs.
 */
function getUnheldSectionIds (mediaDiff) {
  const unheldSections = []
  mediaDiff.changed.forEach(changedMedia => {
    const { media, changes } = changedMedia
    // We know this was an unhold operation, so we only need to check that the
    //    media section started sending media to know that a track was added here.
    if (changes.sending === 'START') {
      const id = typeof media.sectionId === 'undefined' ? media.sectionIndex : media.sectionId
      unheldSections.push(id)
    }
  })
  return unheldSections
}

/**
 * Get the media sections of an SDP where media was added by a remote "unhold"
 *    SDK operation. This function covers the special cases for interop with 3.x/Mobile.
 *    Refer to `was3xUnhold` function for more details.
 * @param {MediaDiff} mediaDiff
 * @return {Array<string>} List of media section IDs.
 */
function get3xUnheldSectionIds (mediaDiff) {
  const unheldSections = []
  // Special cases for 3.x/Mobile; refer to `was3xUnhold` function for more details.
  //    Summary: all changed media went from 'sendonly' to 'sendrecv'
  mediaDiff.changed.forEach(({ media, changes }) => {
    if (
      (changes.sending === 'SAME' && changes.receiving === 'START') ||
      /*
       * Special-case: If the media is video, it's possible to go from "inactive" to "sendrecv".
       *    This can happen when the SDK is receiving MoH then is unheld immediately (without
       *    going through a stop MoH operation).
       * This behaviour was seen when the remote endpoint was a SIP device. See KAA-2593.
       */
      (media.type === 'video' && changes.sending === 'START' && changes.receiving === 'START')
    ) {
      const id = typeof media.sectionId === 'undefined' ? media.sectionIndex : media.sectionId
      unheldSections.push(id)
    }
  })
  return unheldSections
}

/**
 * Get the media sections of an SDP where media was added by a remote "add media"
 *    SDK operation.
 * @param {MediaDiff} mediaDiff
 * @return {Array<string>} List of media section IDs.
 */
function getAddedSectionIds (mediaDiff) {
  let addedMediaSections = []
  mediaDiff.changed.forEach(changedMedia => {
    const { media, changes } = changedMedia
    // This ChangedMedia object represents a "track added".
    if (changes.sending === 'START' && (changes.receiving === 'SAME' || changes.receiving === 'START')) {
      const id = typeof media.sectionId === 'undefined' ? media.sectionIndex : media.sectionId
      addedMediaSections.push(id)
    }
  })

  // For the Transceiver for newly added media sections to exist, the SDK has to
  //    have handled the remote offer at the WebRTC-layer already.
  const newSectionsWithTracks = mediaDiff.added
    .filter(mediaSummary => mediaSummary.willSend)
    .map(mediaSummary => {
      // If the sectionId is undefined, return the index instead.
      // The order of media sections (their index) /should/ never change.
      return typeof mediaSummary.sectionId === 'undefined' ? mediaSummary.sectionIndex : mediaSummary.sectionId
    })

  addedMediaSections = addedMediaSections.concat(newSectionsWithTracks)

  return addedMediaSections
}

/**
 * Get the media sections of an SDP where media was removed by a remote "remove media"
 *    SDK operation.
 * @param {MediaDiff} mediaDiff
 * @return {Array<string>} List of media section IDs.
 */
function getRemovedSectionIds (mediaDiff) {
  // Get the list of media section Ids that media was removed from.
  const removedMediaSections = []
  mediaDiff.changed.forEach(changedMedia => {
    const { media, changes } = changedMedia
    // This ChangedMedia object represents a "track removed".
    if (changes.sending === 'STOP' && changes.receiving !== 'STOP') {
      const id = typeof media.sectionId === 'undefined' ? media.sectionIndex : media.sectionId
      removedMediaSections.push(id)
    }
  })

  return removedMediaSections
}

/**
 * Get the list of all remote tracks currently being received for a Session.
 * @param {Session} session A WebRTC-stack Session object.
 * @return {Array<string>} List of remote track IDs.
 */
export function * getAllRemoteTracks (session) {
  const affectedTracks = yield call([session, 'getActiveRemoteTrackIds'])
  return affectedTracks
}

/**
 * Get the list of all incoming remote tracks not yet being received for a Session.
 * @param {Session} session A WebRTC-stack Session object.
 * @return {Array<string>} List of remote track IDs.
 */
export function * getIncomingRemoteTracks (session) {
  const incomingTracks = yield call([session, 'getIncomingRemoteTrackIds'])
  return incomingTracks
}
